import { Component, Input, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LearningPlanStatusComponent } from '../learning-plan-status/learning-plan-status.component';
import { LearningPlanStatus } from '../../models/LearningPlanDTO';

@Component({
  selector: 'app-learning-plan-statistics',
  imports: [
    DatePipe,
    TranslocoDirective,
    TranslocoPipe,
    MatIconModule,
    MatTooltipModule,
    LearningPlanStatusComponent,
  ],
  templateUrl: './learning-plan-statistics.component.html',
})
export class LearningPlanStatisticsComponent implements OnInit {
  @Input({ required: true }) startAt = '';
  @Input({ required: true }) phaseDuration = 'year';
  @Input({ required: true }) numberOfEnrolledStudents = 0;
  @Input({ required: true }) numberOfEnrolledStudentsLearningPlanStarted = 0;
  @Input({ required: true }) numberOfCompletedStudents = 0;
  @Input() status?: LearningPlanStatus;
  @Input() phaseStart?: string;
  @Input() phaseEnd?: string;
  @Input() isLooping = false;
  @Input() deadline?: string | null;
  @Input() isDeadlinePassed = false;

  phaseStartDate: Date | null;
  phaseEndDate: Date | null;

  ngOnInit() {
    if (this.phaseStart && this.phaseEnd) {
      this.phaseStartDate = new Date(this.phaseStart);
      this.phaseEndDate = new Date(this.phaseEnd);
    } else {
      this.phaseStartDate = this.calculatePhaseStart();
      this.phaseEndDate = this.calculatePhaseEnd();
    }
  }

  public get completionRate(): number {
    if (this.numberOfEnrolledStudents === 0) {
      return 0;
    }
    return Math.round(
      (this.numberOfCompletedStudents / this.numberOfEnrolledStudents) * 100,
    );
  }

  public get started(): boolean {
    return Date.now() >= new Date(this.startAt).getTime();
  }

  public get durationTranslationKey(): string {
    return this.phaseDuration
      ? `portal.trainings.detailPage.period.${this.phaseDuration}`
      : '';
  }

  public get repeatingStatusTranslationKey(): string {
    return this.isLooping
      ? 'portal.trainings.detailPage.repetition.active'
      : 'portal.trainings.detailPage.repetition.inactive';
  }

  public get repeatingTypeKey(): string {
    return this.isLooping
      ? 'portal.trainings.detailPage.startsFromBeginning'
      : 'portal.trainings.detailPage.doesNotStartFromBeginning';
  }

  private calculatePhaseStart(): Date | null {
    const duration = this.phaseDuration;
    const startDate = new Date(this.startAt);
    const today = new Date();

    if (!this.started) {
      return null;
    }

    switch (duration) {
      case 'year':
        return new Date(today.getFullYear(), startDate.getMonth(), 1);
      case 'quarter':
        return new Date(
          today.getFullYear(),
          Math.floor(today.getMonth() / 3) * 3,
          1,
        );
      case 'month':
        return new Date(today.getFullYear(), today.getMonth(), 1);
      default:
        return startDate;
    }
  }

  private calculatePhaseEnd(): Date | null {
    const duration = this.phaseDuration;
    const startDate = new Date(this.startAt);
    const today = new Date();

    if (!this.started) {
      return null;
    }

    switch (duration) {
      case 'year':
        return new Date(today.getFullYear() + 1, startDate.getMonth(), 1);
      case 'quarter':
        return new Date(
          today.getFullYear(),
          Math.floor(today.getMonth() / 3) * 3 + 3,
          1,
        );
      case 'month':
        return new Date(today.getFullYear(), today.getMonth() + 1, 1);
      default:
        return startDate;
    }
  }
}
